import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormTextArea from "../../components/formFields/FormTextArea";
import { closed } from "../../components/drawer/drawerSlice";
import IconButton from "../../components/common/IconButton";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { clearBlogFAQDetails } from "./blogSlice";
import {
  createBlogFAQ,
  updateBlogFAQ,
  getAllBlogFAQ,
  getBlogFAQByID,
} from "./services/blog.services";

const CreateBlogFAQ: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const BlogID = useAppSelector((state) => state.blog.BlogID);
  const BlogFAQDetail = useAppSelector((state) => state.blog.BlogFAQDetail);
  const BlogFAQID = useAppSelector((state) => state.drawer.data) as
    | number
    | null;
  const loading = useAppSelector((state) => state.blog.formLoading);

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey((prev) => prev + 1);
  }, [BlogFAQDetail]);

  useEffect(() => {
    return () => {
      dispatch(clearBlogFAQDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (BlogFAQID) {
      dispatch(getBlogFAQByID(BlogFAQID));
    }
  }, [BlogFAQID]);

  const handleSubmit = async (values: any) => {
    if (!BlogID) return;
    try {
      if (!BlogFAQID) {
        const insertPayload = {
          BlogID: BlogID,
          Answer: values?.Answer ? values?.Answer : "",
          Question: values?.Question ? values?.Question : "",
          SequenceNo: values.SequenceNo ? +values.SequenceNo : 0,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createBlogFAQ(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearBlogFAQDetails());
          dispatch(getAllBlogFAQ(BlogID));
        }
      } else {
        const editPayload = {
          ID: BlogFAQID,
          BlogID: BlogID,
          Answer: values?.Answer ? values?.Answer : "",
          Question: values?.Question ? values?.Question : "",
          SequenceNo: values.SequenceNo ? +values.SequenceNo : 0,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateBlogFAQ(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearBlogFAQDetails());
          dispatch(getAllBlogFAQ(BlogID));
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
    dispatch(clearBlogFAQDetails());
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        onSubmit={handleSubmit}
        initialValues={
          BlogFAQDetail || {
            Question: "",
            Answer: "",
            SequenceNo: "",
            IsActive: 1,
          }
        }
        key={formKey}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
              <GridLayoutItem
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 20,
                }}
              >
                <Typography.h4 className="m-0">
                  {BlogFAQID ? "Update Blog FAQ" : "Add Blog FAQ"}
                </Typography.h4>

                <IconButton
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={handleFormClose}
                  icon={RxCross1}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  name="Question"
                  label="Question"
                  placeholder="Question"
                  component={FormTextArea}
                  astrike={true}
                  validator={requiredValidator}
                  style={{ height: 100 }}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  name="Answer"
                  label="Answer"
                  placeholder="Answer"
                  component={FormTextArea}
                  validator={requiredValidator}
                  astrike={true}
                  style={{ height: 100 }}
                />
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  wrapperClassName="right-aligned-field"
                  name="SequenceNo"
                  label="Sequence No."
                  placeholder="0"
                  format="n0"
                  component={FormNumericTextField}
                />
              </GridLayoutItem>

              <GridLayoutItem>
                <Field
                  id={"IsActive"}
                  name={"IsActive"}
                  label={"Status"}
                  placeholder="i.e. Active"
                  component={FormSelectionField}
                  validator={requiredValidator}
                  astrike={true}
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status.value,
                      label: status.label,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  marginTop: "20px",
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={BlogFAQID ? "Update" : "Save"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || loading}
                  loading={loading}
                />

                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateBlogFAQ;
